import { useState } from 'react';
import { signInWithPopup, GoogleAuthProvider, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp, collection, addDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import LanguageSwitcher from '../common/LanguageSwitcher';
import './Login.css';

export default function Login() {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [form, setForm] = useState({
    email: '',
    password: '',
    displayName: ''
  });
  const navigate = useNavigate();

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let userCredential;
      
      if (isRegister) {
        // Regisztráció
        userCredential = await createUserWithEmailAndPassword(
          auth,
          form.email,
          form.password
        );

        // User dokumentum létrehozása
        const userRef = doc(db, 'users', userCredential.user.uid);
        await setDoc(userRef, {
          email: form.email,
          displayName: form.displayName || form.email.split('@')[0],
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp()
        });

        // Ügyfél dokumentum létrehozása a customers kollekcióban
        const customerData = {
          name: form.displayName || form.email.split('@')[0],
          primaryContact: {
            email: form.email,
            firstName: form.displayName ? form.displayName.split(' ')[0] : '',
            lastName: form.displayName ? form.displayName.split(' ').slice(1).join(' ') : '',
            phone: ''
          },
          status: 'Új partner',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          userId: userCredential.user.uid
        };

        await addDoc(collection(db, 'customers'), customerData);
      } else {
        // Bejelentkezés
        userCredential = await signInWithEmailAndPassword(
          auth,
          form.email,
          form.password
        );

        // Utolsó bejelentkezés frissítése
        const userRef = doc(db, 'users', userCredential.user.uid);
        await setDoc(userRef, {
          lastLogin: serverTimestamp()
        }, { merge: true });
      }

      // Sikeres bejelentkezés/regisztráció után navigálás a főoldalra
      navigate('/');
      
    } catch (err) {
      console.error('Auth hiba:', err);
      setError(
        err.code === 'auth/user-not-found' ? 'Hibás email vagy jelszó' :
        err.code === 'auth/wrong-password' ? 'Hibás email vagy jelszó' :
        err.code === 'auth/email-already-in-use' ? 'Ez az email cím már regisztrálva van' :
        err.code === 'auth/weak-password' ? 'A jelszó túl gyenge' :
        'Hiba történt a művelet során'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      const user = await signInWithGoogle();
      if (user) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Hiba történt a Google bejelentkezés során');
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    
    // Create/update user document
    const userRef = doc(db, 'users', result.user.uid);
    const userSnap = await getDoc(userRef);
    const isNewUser = !userSnap.exists();
    
    await setDoc(userRef, {
      email: result.user.email,
      displayName: result.user.displayName || '',
      photoURL: result.user.photoURL || '',
      lastLogin: serverTimestamp()
    }, { merge: true });
    
    // Ha új felhasználó, akkor létrehozzuk a customers kollekcióban is
    if (isNewUser) {
      const customerData = {
        name: result.user.displayName || result.user.email.split('@')[0],
        primaryContact: {
          email: result.user.email,
          firstName: result.user.displayName ? result.user.displayName.split(' ')[0] : '',
          lastName: result.user.displayName ? result.user.displayName.split(' ').slice(1).join(' ') : '',
          phone: ''
        },
        status: 'Új partner',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        userId: result.user.uid
      };

      await addDoc(collection(db, 'customers'), customerData);
    }
    
    return result.user;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (!resetEmail) {
        setError('Kérjük, adja meg az email címét');
        return;
      }

      await sendPasswordResetEmail(auth, resetEmail);
      setSuccess('Jelszó-visszaállító email elküldve. Kérjük, ellenőrizze a postaládáját.');
      setShowForgotPassword(false);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(
        err.code === 'auth/user-not-found' ? 'Nem található felhasználó ezzel az email címmel' :
        err.code === 'auth/invalid-email' ? 'Érvénytelen email cím' :
        'Hiba történt a jelszó-visszaállítás során'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative bg-gray-900">
      <div className="animated-gradient" />
      
      <div className="absolute top-6 right-6 z-20">
        <div className="scale-125">
          <LanguageSwitcher />
        </div>
      </div>

      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-xl relative z-10">
        <div className="flex justify-center">
          <div className="flex items-center space-x-2">
            <div className="bg-indigo-600 p-2 rounded">
            <svg width="162" height="30" viewBox="0 0 324 59" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M0.3025 47V5.784H33.2625V16.92H15.7905V20.632H30.8305V32.152H15.7905V35.864H33.2625V47H0.3025ZM47.9625 47.64C43.4825 47.64 40.3252 46.744 38.4905 44.952C36.6985 43.16 35.8025 40.3867 35.8025 36.632V3.864H51.2905V34.584C51.2905 35.3947 51.4398 35.992 51.7385 36.376C52.0798 36.76 52.6772 36.952 53.5305 36.952C54.7252 36.952 55.7918 36.76 56.7305 36.376V45.976C55.4078 46.5307 54.1492 46.936 52.9545 47.192C51.7598 47.4907 50.0958 47.64 47.9625 47.64ZM60.6465 13.592L66.7265 0.599998H79.9105L72.6145 13.592H60.6465ZM77.9265 13.592L84.0065 0.599998H97.1905L89.8945 13.592H77.9265ZM77.0945 47.64C70.6518 47.64 65.8518 46.3173 62.6945 43.672C59.5798 41.0267 58.0225 37.08 58.0225 31.832C58.0225 26.584 59.5798 22.6373 62.6945 19.992C65.8518 17.3467 70.6518 16.024 77.0945 16.024C89.8518 16.024 96.2305 21.2933 96.2305 31.832C96.2305 42.3707 89.8518 47.64 77.0945 47.64ZM77.0945 36.568C78.2038 36.568 79.0358 36.2267 79.5905 35.544C80.1452 34.8613 80.4225 33.624 80.4225 31.832C80.4225 30.04 80.1452 28.8027 79.5905 28.12C79.0358 27.4373 78.2038 27.096 77.0945 27.096C76.0278 27.096 75.2172 27.4373 74.6625 28.12C74.1078 28.8027 73.8305 30.04 73.8305 31.832C73.8305 33.624 74.1078 34.8613 74.6625 35.544C75.2172 36.2267 76.0278 36.568 77.0945 36.568ZM99.4275 47V16.664H113.956L114.276 18.392C115.385 17.7947 116.878 17.2613 118.756 16.792C120.676 16.28 122.745 16.024 124.964 16.024C128.59 16.024 131.214 16.984 132.836 18.904C134.5 20.7813 135.332 23.64 135.332 27.48V47H119.844V29.464C119.844 28.0987 119.14 27.416 117.732 27.416C117.305 27.416 116.793 27.544 116.196 27.8C115.641 28.056 115.214 28.3547 114.916 28.696V47H99.4275ZM150.281 58.264C148.19 58.264 146.484 58.1573 145.161 57.944C143.881 57.7307 142.58 57.3467 141.257 56.792V46.936C142.494 47.3627 143.774 47.576 145.097 47.576C146.377 47.576 147.444 47.4693 148.297 47.256C149.15 47.0853 149.876 46.68 150.473 46.04L136.585 16.664H152.777L158.089 31.768L163.145 16.664H179.273L168.841 43.352C167.006 48.0027 164.617 51.6507 161.673 54.296C158.729 56.9413 154.932 58.264 150.281 58.264ZM181.178 47V5.784H196.666V17.304L205.69 5.784H223.162L209.082 22.872L224.442 47H208.058L199.61 32.664L196.666 36.248V47H181.178ZM237.213 47.64C232.733 47.64 229.575 46.744 227.741 44.952C225.949 43.16 225.053 40.3867 225.053 36.632V3.864H240.541V34.584C240.541 35.3947 240.69 35.992 240.989 36.376C241.33 36.76 241.927 36.952 242.781 36.952C243.975 36.952 245.042 36.76 245.981 36.376V45.976C244.658 46.5307 243.399 46.936 242.205 47.192C241.01 47.4907 239.346 47.64 237.213 47.64ZM257.744 47.64C254.33 47.64 251.621 46.6373 249.616 44.632C247.61 42.6267 246.608 39.704 246.608 35.864V16.664H262.096V34.2C262.096 35.5653 262.8 36.248 264.208 36.248C264.677 36.248 265.189 36.12 265.744 35.864C266.298 35.608 266.725 35.3093 267.024 34.968V16.664H282.512V47H267.984L267.664 45.272C266.554 45.9547 265.125 46.5307 263.376 47C261.669 47.4267 259.792 47.64 257.744 47.64ZM309.646 47.64C307.811 47.64 306.211 47.4053 304.846 46.936C303.481 46.4667 302.243 45.8267 301.134 45.016L300.43 47H286.99V3.864H302.478V17.176C303.289 16.8347 304.313 16.5573 305.55 16.344C306.787 16.1307 307.961 16.024 309.07 16.024C313.763 16.024 317.411 17.1973 320.014 19.544C322.659 21.8907 323.982 25.9013 323.982 31.576C323.982 36.9947 322.659 41.0267 320.014 43.672C317.411 46.3173 313.955 47.64 309.646 47.64ZM305.166 36.568C306.105 36.568 306.83 36.248 307.342 35.608C307.897 34.9253 308.174 33.6667 308.174 31.832C308.174 29.9973 307.897 28.76 307.342 28.12C306.83 27.4373 306.105 27.096 305.166 27.096C304.142 27.096 303.246 27.416 302.478 28.056V35.608C302.905 35.9067 303.31 36.1413 303.694 36.312C304.121 36.4827 304.611 36.568 305.166 36.568Z" fill="white"/>
</svg>
            </div>
            
          </div>
        </div>

        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-gray-900">
            {t('login.welcome')}
          </h2>
          <p className="text-sm text-gray-600">
            {t('login.description')}
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="rounded-md bg-green-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <div className="text-sm text-green-700">{success}</div>
              </div>
            </div>
          </div>
        )}

        {showForgotPassword ? (
          <form className="mt-8 space-y-6" onSubmit={handleForgotPassword}>
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Jelszó-emlékeztető kérése</h3>
              <p className="text-sm text-gray-600 mb-4">
                Kérjük, adja meg az email címét, amellyel regisztrált, és küldünk egy jelszó-visszaállító linket.
              </p>
              <div className="rounded-md shadow-sm">
                <label htmlFor="reset-email" className="sr-only">Email cím</label>
                <input
                  id="reset-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Email cím"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setError(null);
                  setSuccess(null);
                }}
                className="py-2 px-4 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Vissza a bejelentkezéshez
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className={`py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                  isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                }`}
              >
                {isLoading ? 'Folyamatban...' : 'Jelszó-emlékeztető küldése'}
              </button>
            </div>
          </form>
        ) : (
          <>
            <form className="mt-8 space-y-6" onSubmit={handleEmailAuth}>
              <div className="rounded-md shadow-sm -space-y-px">
                {isRegister && (
                  <div>
                    <label htmlFor="displayName" className="sr-only">Név</label>
                    <input
                      id="displayName"
                      name="displayName"
                      type="text"
                      required
                      className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                      placeholder="Név"
                      value={form.displayName}
                      onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                    />
                  </div>
                )}
                <div>
                  <label htmlFor="email" className="sr-only">Email cím</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className={`appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 ${!isRegister ? 'rounded-t-md' : ''} focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm`}
                    placeholder="Email cím"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div>
                  <label htmlFor="password" className="sr-only">Jelszó</label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete={isRegister ? 'new-password' : 'current-password'}
                    required
                    className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                    placeholder="Jelszó"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                </div>
              </div>

              {!isRegister && (
                <div className="flex items-center justify-end">
                  <div className="text-sm">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(true);
                        setResetEmail(form.email);
                        setError(null);
                        setSuccess(null);
                      }}
                      className="font-medium text-indigo-600 hover:text-indigo-500"
                    >
                      Elfelejtette a jelszavát?
                    </button>
                  </div>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                    isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                  }`}
                >
                  {isLoading ? 'Folyamatban...' : (isRegister ? 'Regisztráció' : 'Bejelentkezés')}
                </button>
              </div>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-50 text-gray-500">vagy</span>
                </div>
              </div>

              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setIsRegister(!isRegister)}
                  className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                  {isRegister ? 'Már van fiókom' : 'Új fiók létrehozása'}
                </button>
              </div>

              <div className="mt-3">
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={isLoading}
                  className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                  <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                    <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                    <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
                    <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
                  </svg>
                  Bejelentkezés Google fiókkal
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
} 